import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { mobileLarge } from "clutch/src/Style/style.mjs";
import { Tag } from "clutch/src/Tag/Tag.js";

import { IS_APP } from "@/__main__/constants.mjs";
import {
  ACTIVE_GAMES,
  GAME_PREFERRED_NAME_MAP,
  GAME_SHORT_NAMES,
  I18N_PLACEHOLDER,
} from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import { getGameDefinition } from "@/app/games.mjs";
import { getNavProfile, useCurrentAccount } from "@/app/util.mjs";
import { GAME_LIBRARY_IMAGES } from "@/library/constants.mjs";
import Ripple from "@/shared/Ripple.jsx";
import Tilting from "@/shared/Tilting.jsx";
import { classNames } from "@/util/class-names.mjs";

const Game = styled("a")`
  display: flex;
  padding: var(--sp-2) 0;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-1);
  flex: 1 0 0;

  .title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    align-self: stretch;
    ${mobileLarge} {
      flex-direction: column;
      align-items: flex-start;
      jusitfy-content: center;
    }
  }

  .game-title {
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    color: var(--shade0);
  }

  .card:hover {
    outline-color: var(--shade0-25);
  }
`;

const GameImg = styled("div", forwardRef)`
  --radii: var(--br-lg);
  position: relative;
  aspect-ratio: 0.75;
  align-self: stretch;
  border-radius: var(--radii);
  outline: solid 2px transparent;
  outline-offset: var(--sp-0_5);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    box-shadow: inset 0 0 0 1px var(--shade0-15);
    border-radius: var(--radii);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--radii);
  }
`;

export default function GameTile({
  gameSymbol,
  textClassName,
  onClick,
  showTitle = true,
}: {
  gameSymbol: GameSymbol;
  textClassName?: string;
  onClick?: () => void;
  showTitle?: boolean;
}) {
  const { t } = useTranslation();
  const gameDef = getGameDefinition(gameSymbol);
  const account = useCurrentAccount(gameSymbol);
  const isActive = ACTIVE_GAMES.includes(gameSymbol);

  const accountURL =
    account && getNavProfile({ gameSymbol, account })?.profileUrl;

  const gameHome = `/${GAME_SHORT_NAMES[gameSymbol]}`;
  const href = !IS_APP
    ? gameHome
    : isActive && accountURL
      ? accountURL
      : gameDef?.navLinks?.[0]?.href || gameHome;

  return (
    <Game href={href} onClick={onClick}>
      <Tilting className={"w-full"}>
        <GameImg className="card">
          <img
            src={GAME_LIBRARY_IMAGES[gameSymbol]?.keyart}
            alt={gameDef ? t(...gameDef.fullName) : ""}
            width="195"
            height="260"
          />
          <Ripple />
        </GameImg>
      </Tilting>
      {showTitle ? (
        <div className="title-wrapper">
          <span
            {...classNames("game-title", textClassName ?? "type-form--button")}
          >
            {GAME_PREFERRED_NAME_MAP[gameSymbol]
              ? t(...GAME_PREFERRED_NAME_MAP[gameSymbol])
              : I18N_PLACEHOLDER}
          </span>
          {!isActive && !gameDef.hideComingSoon && (
            <Tag
              size="xs"
              color="var(--shade2)"
              text={t("common:comingSoonNormal", "Coming Soon")}
            />
          )}
        </div>
      ) : null}
    </Game>
  );
}
